import { GeneralTable } from "@/shared/components/ui";
import { Auditorialog } from "../../types";
import {
  formatDate,
  getUrlParamsFetch,
  gridSizeMdLg1,
  gridSizeMdLg4,
  gridSizeMdLg6, hasPermission,
  TABLE_CONSTANTS_COLUMS, urlBackend,
  useTableFilter, useTableServerSideFiltering,
  useUploadFile
} from "@/shared";
import {
  createAuditorialogExcelAsync,
  getAuditorialogExcelStatus,
  useFetchGetAuditorialog
} from "@/shared/actions/auditoria/auditorialog.actions";
import { PermissionsEnum } from "@/auth/interfaces";
import { useTableColumnsAuditorialog } from "../../hooks";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { AuditoriaDetalle } from "@/erp/administration/auditoria/components/AuditoriaDetalle";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Grid, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import DateRangePicker from "@/shared/components/ui/CustomSearch/DateRangePicker";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi"
import Flatpickr from 'react-flatpickr';
import CustomAutocompleteFetchForm from "@/shared/components/ui/CustomAutocompleteFetchForm";
import { Empleado } from "@/shared/interfaces/erp/empleados";
import useAutocompleteFetch from "@/shared/hooks/useAutocompleteFetch";
import { useForm } from "react-hook-form";
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { toast } from "react-toastify";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const MAX_EXCEL_STATUS_ATTEMPTS = 900;
const AUDITORIALOG_EXCEL_TASK_STORAGE_KEY = 'auditorialog_excel_task_id';

export default function TableAuditorialog() {

  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [excelProgress, setExcelProgress] = useState(0);
  const [excelStatusMessage, setExcelStatusMessage] = useState('');
  const flatpickrRef = useRef<Flatpickr>(null);
  const hasResumedExcelTaskRef = useRef(false);

  const form = useForm<any>({
    defaultValues: {
      username_search: null,
    }
  });

  const {
    control,
    reset
  } = form

  const {
    pagination,
    setPagination
  } = useTableFilter();

  const {
    pageIndex,
    pageSize
  } = pagination;

  const {
    filterObject,
    columnFilters,
    setColumnFilters
  } = useTableServerSideFiltering();


  const {
    isLoading,
    data,
    isRefetching,
    refetch,
  } = useFetchGetAuditorialog({
    params: {
      usuario_username: form.getValues().usuario_username,
      fecha_inicio: form.getValues().fecha_inicio,
      fecha_fin: form.getValues().fecha_fin,
      page: pageIndex + 1,
      page_size: pageSize,
      ...filterObject,
      filterByState: false,
    }
  });

  const {
    columns
  } = useTableColumnsAuditorialog()

  const {
    fetchAutocompleteEmpleado
  } = useAutocompleteFetch();

  const onEdit = (value: Auditorialog) => {
    setSelectedLogId(value.id);
  }

  const handleCloseDetalle = () => {
    setSelectedLogId(null);
    refetch();
  };

  const {
    downloadExcelFileWithAxios
  } = useUploadFile();

  const clearExcelProgress = () => {
    setTimeout(() => {
      setExcelProgress(0);
      setExcelStatusMessage('');
    }, 2500);
  };

  const pollExcelTask = useCallback(async (taskId: string) => {
    setIsDownloadingExcel(true);
    setExcelProgress((currentProgress) => Math.max(currentProgress, 1));

    try {
      for (let attempt = 0; attempt < MAX_EXCEL_STATUS_ATTEMPTS; attempt++) {
        await wait(2000);

        const statusResponse = await getAuditorialogExcelStatus(taskId);
        const task = statusResponse.data;
        const progress = typeof task.progress === 'number' ? task.progress : 0;

        setExcelProgress((currentProgress) => Math.max(currentProgress, progress));
        setExcelStatusMessage(task.message || 'Generando Excel');

        if (task.state === 'SUCCESS' && task.task_status !== 'error') {
          setExcelProgress(100);
          setExcelStatusMessage('Descargando archivo');
          await downloadExcelFileWithAxios(
            `${urlBackend}api/v1/descargar-excel-auditorialog/${taskId}/`,
            task.filename || `auditorialog_${new Date().toISOString()}.xlsx`
          );
          toast.success('Excel generado y descargado correctamente.');
          localStorage.removeItem(AUDITORIALOG_EXCEL_TASK_STORAGE_KEY);
          return;
        }

        if (task.state === 'FAILURE' || task.task_status === 'error') {
          localStorage.removeItem(AUDITORIALOG_EXCEL_TASK_STORAGE_KEY);
          throw new Error(task.error || 'No se pudo generar el Excel.');
        }
      }

      toast.warning('El Excel sigue generándose. Aplica filtros o vuelve a intentar más tarde si exportas todos los registros.');

    } catch (error) {
      toast.error('Error al generar el Excel de auditoría.');
      console.log(error);
    } finally {
      setIsDownloadingExcel(false);
      clearExcelProgress();
    }
  }, [downloadExcelFileWithAxios]);

  useEffect(() => {
    if (hasResumedExcelTaskRef.current) return;

    const pendingTaskId = localStorage.getItem(AUDITORIALOG_EXCEL_TASK_STORAGE_KEY);
    if (!pendingTaskId) return;

    hasResumedExcelTaskRef.current = true;
    setExcelStatusMessage('Retomando generación pendiente');
    toast.info('Se retomó la generación del Excel pendiente.');
    pollExcelTask(pendingTaskId);
  }, [pollExcelTask]);

  const downloadKardexExcel = async () => {
    if (isDownloadingExcel) return;

    const pendingTaskId = localStorage.getItem(AUDITORIALOG_EXCEL_TASK_STORAGE_KEY);
    if (pendingTaskId) {
      toast.info('Ya existe un Excel generándose. Retomando seguimiento.');
      await pollExcelTask(pendingTaskId);
      return;
    }

    try {
      setIsDownloadingExcel(true);
      setExcelProgress(1);
      setExcelStatusMessage('Iniciando generación del Excel');
      toast.info('Generando Excel en segundo plano. Si exportas todos los registros puede tardar varios minutos.');

      const filters = Object.fromEntries(
        new URLSearchParams(getUrlParamsFetch(form.getValues())).entries()
      );
      const createResponse = await createAuditorialogExcelAsync(filters);
      const taskId = createResponse.data.task_id;

      if (!taskId) {
        throw new Error('No se recibió el identificador de la tarea.');
      }

      localStorage.setItem(AUDITORIALOG_EXCEL_TASK_STORAGE_KEY, taskId);
      await pollExcelTask(taskId);

    } catch (error) {
      localStorage.removeItem(AUDITORIALOG_EXCEL_TASK_STORAGE_KEY);
      toast.error('Error al generar el Excel de auditoría.');
      console.log(error);
      setIsDownloadingExcel(false);
      clearExcelProgress();
    }
  }

  return <>
    <Grid
      paddingTop={2}
      container spacing={3}
      justifyContent={'space-between'}
      alignItems={'center'}
      justifyItems={'center'}
      alignContent={'center'}
    >

      <Grid item container spacing={3} {...gridSizeMdLg6}>

        <CustomAutocompleteFetchForm<Empleado>
          required={false}
          control={control}
          size={gridSizeMdLg6}
          name="usuario_username"
          label="Usuario"
          fetchData={fetchAutocompleteEmpleado}
          getOptionLabel={(opt) => opt.user || ''}
          key={'autocomplete-empleado'}
          getOptionKey={(o) => o.user!}
          keySearch="user"
          onChangeValue={(e) => {

            reset({
              ...form.getValues(),
              usuario_username: e as string
            })


          }}
        />

        <DateRangePicker
          size={gridSizeMdLg6}
          flatpickrRef={flatpickrRef}
          onChange={(range) => {
            console.log(range);

            if (range.length == 2) {
              reset({
                ...form.getValues(),
                fecha_inicio: formatDate(range[0]),
                fecha_fin: formatDate(range[1])
              })

            }

          }}
        />

      </Grid>

      <Grid container item {...gridSizeMdLg6} justifyContent={'flex-end'}>

        <Grid item {...gridSizeMdLg4}>
          <Button
            size="medium"
            fullWidth
            startIcon={<PiMicrosoftExcelLogoFill />}
            variant="contained"
            color="success"
            disabled={isDownloadingExcel}
            onClick={() =>
              downloadKardexExcel()
            }
          >
            {isDownloadingExcel ? 'GENERANDO...' : 'EXCEL'}
          </Button>

          {isDownloadingExcel && (
            <>
              <LinearProgress
                variant="determinate"
                value={excelProgress}
                sx={{ mt: 1 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {Math.round(excelProgress)}% - {excelStatusMessage}
              </Typography>
            </>
          )}

        </Grid>

        <Grid item {...gridSizeMdLg1}>
          <Tooltip title="Limpiar filtros">
            <IconButton onClick={
              () => {
                reset({
                  usuario_username: null,
                  fecha_inicio: null,
                  fecha_fin: null
                })
                refetch()
                flatpickrRef.current?.flatpickr.clear();
              }
            }>
              <FilterAltOffIcon />
            </IconButton>
          </Tooltip>
        </Grid>

      </Grid>

      <Grid item xs={12}>
        <GeneralTable<Auditorialog>
          columns={columns}
          data={data?.data?.data || []}
          isLoading={isLoading}
          isRefetching={isRefetching}
          // search
          enableGlobalFilter={false}
          // filters
          enableManualFiltering={true}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          // pagination
          pagination={pagination}
          onPaging={setPagination}
          rowCount={data?.data?.count}
          // actions
          enableColumnActions={hasPermission(PermissionsEnum.api_change_auditorialog)}
          actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
          positionActionsColumn="first"
          canEdit={hasPermission(PermissionsEnum.api_change_auditorialog)}
          onEdit={onEdit}
          editIcon={<OpenInNewIcon />}
          canDelete={false}
        />
      </Grid>
    </Grid>


    {selectedLogId && (
      <AuditoriaDetalle
        logId={selectedLogId}
        open={!!selectedLogId}
        onClose={handleCloseDetalle}
      />
    )}
  </>
}
